import { Container, Graphics } from 'pixi.js';
import type { ParticleSnapshot } from '@nebulark/engine';
import { SpritePool } from './spritePool';
import { makeParticleSprite } from './factories';

/**
 * Simple particle layer. Reconciles from snapshot.particles each frame using a
 * pooled Graphics circle per particle. Alpha fades with age; size/color come
 * from the snapshot.
 */
export class ParticleLayer {
  readonly container: Container;
  private readonly pool = new SpritePool<Graphics>(makeParticleSprite);
  private readonly live = new Map<number, Graphics>();

  constructor() {
    this.container = new Container();
    this.container.sortableChildren = false;
  }

  update(particles: readonly ParticleSnapshot[]): void {
    const seen = new Set<number>();
    for (const p of particles) {
      seen.add(p.id);
      let g = this.live.get(p.id);
      if (!g) {
        g = this.pool.acquire();
        this.container.addChild(g);
        this.live.set(p.id, g);
      }
      g.x = p.pos.x;
      g.y = p.pos.y;
      const lifeFrac = p.lifeMs > 0 ? Math.max(0, 1 - p.ageMs / p.lifeMs) : 0;
      g.alpha = lifeFrac;
      const s = Math.max(0.25, p.size / 2);
      g.scale.set(s, s);
      g.tint = p.color;
    }

    // Release any particles that vanished this frame.
    for (const [id, g] of this.live) {
      if (!seen.has(id)) {
        this.container.removeChild(g);
        this.pool.release(g);
        this.live.delete(id);
      }
    }
  }
}
